import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Row, Col, Card, Badge, Button, Modal, Form, OverlayTrigger, Tooltip, Popover, ButtonGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getTrips, getDrivers, updateTrip, getVehicles, bulkImportTrips, bulkImportPreview } from '../api';
import { MapPin, Clock, Upload, Bell, Volume2, AlertCircle, Download } from 'lucide-react';
import BulkImportPreviewModal from './BulkImportPreviewModal';
import { downloadBulkImportTemplate } from '../utils/excelTemplateGenerator';
import apiClient from '../../api';


import SearchableSelect from './SearchableSelect';
import { toast } from 'react-toastify';

const getFlagEmoji = (timezone, localCurrencyCode, lat, lng, originBase = null) => {
    let tz = timezone;
    if (!tz) {
        if (localCurrencyCode === 'PEN') tz = 'America/Lima';
        else if (localCurrencyCode === 'ARS') tz = 'America/Argentina/Mendoza';
        else if (localCurrencyCode === 'EUR') tz = 'Europe/Madrid';
        else if (localCurrencyCode === 'UYU') tz = 'America/Montevideo';
        else if (lat < -18 && lat > -56 && lng < -66 && lng > -76) tz = 'America/Santiago';
        else if (lat < 0 && lat > -18 && lng < -68 && lng > -82) tz = 'America/Lima';
        else if (lat >= -35.5 && lat <= -30.0 && lng >= -58.5 && lng <= -53.0) tz = 'America/Montevideo';
        else if (lat >= 36.0 && lat <= 44.0 && lng >= -9.5 && lng <= 4.5) tz = 'Europe/Madrid';
        else tz = 'America/Santiago';
    }
    const tzLower = (tz || '').toLowerCase();
    let countryCode = originBase?.country_code ? originBase.country_code.toLowerCase().trim() : '';

    if (!countryCode) {
        if (tzLower.includes('montevideo') || tzLower.includes('uruguay')) countryCode = 'uy';
        else if (tzLower.includes('madrid') || tzLower.includes('barcelona') || tzLower.includes('spain')) countryCode = 'es';
        else if (tzLower.includes('santiago')) countryCode = 'cl';
        else if (tzLower.includes('argentina') || tzLower.includes('mendoza') || tzLower.includes('buenos_aires')) countryCode = 'ar';
        else if (tzLower.includes('lima')) countryCode = 'pe';
        else if (tzLower.includes('santo_domingo') || tzLower.includes('punta_cana')) countryCode = 'do';
        else if (tzLower.includes('bogota') || tzLower.includes('colombia')) countryCode = 'co';
        else if (tzLower.includes('rio') || tzLower.includes('sao_paulo')) countryCode = 'br';
        else if (localCurrencyCode === 'EUR') countryCode = 'es';
        else if (localCurrencyCode === 'UYU') countryCode = 'uy';
        else if (lat >= -35.5 && lat <= -30.0 && lng >= -58.5 && lng <= -53.0) countryCode = 'uy';
        else if (lat >= 36.0 && lat <= 44.0 && lng >= -9.5 && lng <= 4.5) countryCode = 'es';
        else countryCode = 'cl';
    }

    switch (countryCode) {
        case 'uy': return '🇺🇾';
        case 'es': return '🇪🇸';
        case 'cl': return '🇨🇱';
        case 'ar': return '🇦🇷';
        case 'pe': return '🇵🇪';
        case 'do': return '🇩🇴';
        case 'co': return '🇨🇴';
        case 'br': return '🇧🇷';
        default: return '🇨🇱';
    }
};

const DispatchBoard = ({ isLoaded }) => {
    const navigate = useNavigate();
    const [trips, setTrips] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    // Modal de asignación rápida
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [selectedDriverId, setSelectedDriverId] = useState('');

    // Modal de vista previa e importación masiva
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewStats, setPreviewStats] = useState(null);
    const [previewFileName, setPreviewFileName] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Refs
    const fileInputRef = useRef(null);
    const audioContextRef = useRef(null);
    const [importing, setImporting] = useState(false);

    const STATUS_LABELS = {
        'pending': 'Pendiente',
        'PENDING': 'Pendiente',
        'assigned': 'Asignado',
        'ASSIGNED': 'Asignado',
        'confirmed': 'Confirmado',
        'CONFIRMED': 'Confirmado',
        'en_route': 'En Camino',
        'EN_ROUTE': 'En Camino',
        'arrived': 'En Posición',
        'ARRIVED': 'En Posición',
        'on_board': 'En Ruta',
        'ON_BOARD': 'En Ruta',
        'completed': 'Finalizado',
        'COMPLETED': 'Finalizado',
        'cancelled': 'Cancelado',
        'CANCELLED': 'Cancelado',
        'no_show': 'No Show',
        'NO_SHOW': 'No Show'
    };

    const playAlertSound = useCallback(() => {
        try {
            // Intentar crear o recuperar el contexto de audio
            if (!audioContextRef.current) {
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            const ctx = audioContextRef.current;
            
            if (ctx.state === 'suspended') {
                ctx.resume().catch(err => console.log("Audio bloqueado"));
            }

            const now = ctx.currentTime;
            
            const playTone = (freq, start, duration) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                
                osc.type = 'sawtooth'; 
                osc.frequency.setValueAtTime(freq, start);
                
                gain.gain.setValueAtTime(0.3, start);
                gain.gain.exponentialRampToValueAtTime(0.01, start + duration);
                
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(start);
                osc.stop(start + duration);
            };

            // Secuencia de 6 bips para cualquier alerta (advertencia o crítica)
            for (let i = 0; i < 6; i++) {
                const time = now + (i * 0.15);
                const freq = 880; 
                playTone(freq, time, 0.1);
            }
        } catch (e) { console.error("Audio error:", e); }
    }, []);

    const loadFleetCatalog = useCallback(async () => {
        try {
            const [driversData, vehiclesData] = await Promise.all([
                getDrivers({ limit: 1000 }),
                getVehicles({ limit: 1000 })
            ]);
            setDrivers(driversData);
            setVehicles(vehiclesData);
        } catch (error) {
            console.error("Error cargando catálogo de flota:", error);
        }
    }, []);

    const loadData = useCallback(async () => {
        try {
            // Cada vez que refrescamos datos, intentamos reanudar el audio por si el usuario ya hizo clic
            if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume();
            }

            const tripsData = await getTrips({ status: 'pending,assigned,confirmed,en_route,arrived,on_board,scheduled', limit: 300 });
            setTrips(tripsData);
            setLastUpdated(new Date());
            setLoading(false);
        } catch (error) { console.error("Error cargando tablero:", error); }
    }, []);

    useEffect(() => {
        loadData();
        loadFleetCatalog();
        const interval = setInterval(loadData, 30000); 
        return () => clearInterval(interval);
    }, [loadData, loadFleetCatalog]);

    const processedTrips = useMemo(() => {
        // Obtenemos la hora actual del PC en UTC para comparar manzanas con manzanas
        const now = new Date();
        const nowUTC = now.getTime();
        
        return trips.map(t => {
            const scheduledTime = new Date(t.scheduled_time);
            const scheduledUTC = scheduledTime.getTime();
            const diffMs = scheduledUTC - nowUTC;
            const minutesLeft = Math.floor(diffMs / 60000); 
            
            // 2. DETECCIÓN DE ZONA HORARIA DEL SERVICIO (ROBUSTA)
            let tripTimezone = t.origin_base?.timezone || t.timezone;
            if (!tripTimezone) {
                if (t.local_currency_code === 'PEN') tripTimezone = 'America/Lima';
                else if (t.local_currency_code === 'ARS') tripTimezone = 'America/Argentina/Mendoza';
                else if (t.pickup_lat < -18 && t.pickup_lat > -56 && t.pickup_lng < -66 && t.pickup_lng > -76) tripTimezone = 'America/Santiago';
                else if (t.pickup_lat < 0 && t.pickup_lat > -18 && t.pickup_lng < -68 && t.pickup_lng > -82) tripTimezone = 'America/Lima';
                else tripTimezone = 'America/Santiago';
            }

            // 3. FORMATEO DE HORA Y FECHA LOCAL
            const localTimeStr = new Intl.DateTimeFormat('es-CL', {
                timeZone: tripTimezone,
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            }).format(scheduledTime);

            // 4. HORA ADMINISTRATIVA CHILE
            const adminTimeStr = new Intl.DateTimeFormat('es-CL', {
                timeZone: 'America/Santiago',
                day: '2-digit',
                month: '2-digit',
                year: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            }).format(scheduledTime);

            const isInternational = tripTimezone !== 'America/Santiago';

            // MÁRGENES DE ALERTA
            const criticalThreshold = t.client?.lead_time_alert || 60;
            const warningThreshold = criticalThreshold * 2; 
            
            let alertLevel = 'normal'; 
            let message = '';
            let score = 0;
            const statusLower = t.status?.toLowerCase();

            // Lógica idéntica a Locución (TripsLogView):
            const isWithin48Hours = minutesLeft <= 2880;
            const isDelayed = ['pending', 'assigned', 'confirmed'].includes(statusLower) && scheduledTime < now;
            const isProactiveAlert = ['pending', 'assigned', 'confirmed'].includes(statusLower) && minutesLeft <= criticalThreshold;

            const isCritical = isWithin48Hours && (isDelayed || isProactiveAlert);

            if (isCritical) {
                alertLevel = 'critical';
                const delay = Math.abs(minutesLeft);
                if (isDelayed) {
                    if (statusLower === 'pending') {
                        message = `RETRASADO ${delay} min - SIN CONDUCTOR`;
                        score = 10000 + delay;
                    } else {
                        message = `RETRASADO ${delay} min - NO HA INICIADO`;
                        score = 9000 + delay;
                    }
                } else {
                    message = `Faltan ${minutesLeft} min - ASIGNAR / INICIAR`;
                    score = 2000 - minutesLeft;
                }
            } else if (statusLower === 'pending' && minutesLeft <= warningThreshold && isWithin48Hours) {
                alertLevel = 'warning';
                message = `Faltan ${minutesLeft} min - Sin Conductor`;
                score = 1000 - minutesLeft;
            } else if (statusLower === 'pending' && minutesLeft <= 2880) { // Faltan < 2 días
                alertLevel = 'warning';
                message = `Faltan < 2 días - Sin Conductor`;
                score = 500 - (minutesLeft / 10);
            } else if (['assigned', 'confirmed'].includes(statusLower) && minutesLeft <= warningThreshold && isWithin48Hours) {
                alertLevel = 'warning';
                message = `Faltan ${minutesLeft} min - Sin iniciar`;
                score = 800 - minutesLeft;
            }

            return { ...t, alertLevel, message, minutesLeft, score, localTimeStr, adminTimeStr, tripTimezone, isInternational };
        })
        .filter(t => t.alertLevel !== 'normal') // Filtrar solo alertas
        .sort((a, b) => b.score - a.score || new Date(a.scheduled_time) - new Date(b.scheduled_time)); 
    }, [trips]);

    useEffect(() => {
        // Dispara el sonido SOLAMENTE para alertas críticas (igual que locución)
        const hasCriticalAlerts = processedTrips.some(t => t.alertLevel === 'critical');
        if (hasCriticalAlerts && !loading) {
            playAlertSound();
        }
    }, [processedTrips, loading, playAlertSound]);

    // --- ACCIONES DE IMPORTACIÓN MASIVA CON VISTA PREVIA ---
    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setSelectedFile(file);
        setPreviewFileName(file.name);
        setImporting(true);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const stats = await bulkImportPreview(formData);
            setPreviewStats(stats);
            setShowPreviewModal(true);
        } catch (e) {
            toast.error("Error al analizar el archivo. Revisa el formato del Excel.");
        } finally {
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleConfirmImport = async () => {
        if (!selectedFile) return;
        setPreviewLoading(true);

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const res = await bulkImportTrips(formData);
            toast.success(`¡Éxito! ${res.imported} reservas creadas/actualizadas.`);
            setShowPreviewModal(false);
            loadData();
        } catch (e) {
            toast.error("Error al procesar la importación de reservas.");
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleOpenAssign = (trip) => {
        loadFleetCatalog();
        setSelectedTrip(trip);
        setSelectedDriverId('');
        setShowAssignModal(true);
    };

    const handleAssignDriver = async () => {
        if (!selectedTrip || !selectedDriverId) return;
        try {
            const svcDateStr = selectedTrip.scheduled_time ? selectedTrip.scheduled_time.split("T")[0] : new Date().toISOString().split("T")[0];
            const valRes = await apiClient.get(`transport/drivers/${selectedDriverId}/validate-for-service?service_date=${svcDateStr}`);
            if (valRes.data && !valRes.data.is_valid) {
                toast.error(valRes.data.error_message || "Documento clave vencido. No puedes seleccionar este conductor/vehículo.");
                return;
            }

            await updateTrip(selectedTrip.id, { 
                driver_id: parseInt(selectedDriverId),
                status: 'assigned' 
            });
            setShowAssignModal(false);
            loadData();
        } catch (e) { alert("Error al asignar: " + (e.response?.data?.detail || e.message)); }
    };

    const handleAdvanceStatus = async (trip, nextStatus) => {
        if(!window.confirm(`¿Avanzar estado a ${nextStatus}?`)) return;
        try {
            await updateTrip(trip.id, { status: nextStatus });
            loadData();
        } catch (e) { alert("Error al actualizar"); }
    };

    const handleContactDriver = (driver) => {
        if (!driver) return;
        const msg = `Hola ${driver.full_name}, necesitamos info sobre tu servicio.`;
        const url = `https://wa.me/${driver.phone?.replace('+', '')}?text=${encodeURIComponent(msg)}`;
        window.open(url, '_blank');
    };

    const handleGoToFleetMonitor = (driverId) => {
        navigate('/transport/fleet', { state: { focusDriverId: driverId } });
    };

    // Helper para filtrar conductores por base del viaje
    const getPermittedDrivers = useCallback(() => {
        if (!selectedTrip?.origin_base_id) return drivers.filter(d => d.is_active && d.has_active_session);
        
        return drivers.filter(d => {
            if (!d.is_active || !d.has_active_session) return false;
            const v = vehicles.find(veh => veh.current_driver_id === d.id && veh.is_active);
            if (!v) return false;
            return v.permitted_bases?.some(b => b.id === selectedTrip.origin_base_id);
        });
    }, [selectedTrip, drivers, vehicles]);

    if (loading) return <div className="text-center p-5">Cargando Tablero de Control...</div>;

    const permittedDrivers = getPermittedDrivers();

    return (
        <div className="p-3 bg-light min-vh-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="mb-0 fw-bold text-danger d-flex align-items-center gap-2">
                        <Bell size={28} className="animate-bounce" /> Sala de Control
                    </h3>
                    <div className="d-flex align-items-center gap-2">
                        <small className="text-muted">Monitoreo en tiempo real - {processedTrips.length} alertas</small>
                        <Volume2 size={16} className="text-danger" />
                    </div>
                </div>
                <div className="text-end d-flex align-items-center gap-2">
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        onChange={handleFileSelect} 
                        accept=".xlsx, .xls" 
                        style={{ display: 'none' }} 
                    />
                    <ButtonGroup size="sm">
                        <Button 
                            variant="outline-primary" 
                            className="fw-bold" 
                            onClick={downloadBulkImportTemplate}
                            title="Descargar plantilla limpia de Excel"
                        >
                            <Download size={15} className="me-1" />
                            Plantilla
                        </Button>
                        <Button 
                            variant="primary" 
                            className="fw-bold" 
                            onClick={() => fileInputRef.current?.click()}
                            disabled={importing}
                        >
                            <Upload size={16} className="me-1" />
                            {importing ? "Analizando..." : "Cargar Reservas"}
                        </Button>
                        <Button variant="dark" onClick={loadData}>Refrescar</Button>
                    </ButtonGroup>
                    <span className="badge bg-white text-dark border p-2">{lastUpdated.toLocaleTimeString()}</span>
                </div>
            </div>

            {processedTrips.length === 0 ? (
                <div className="text-center p-5">
                    <div className="display-1">✅</div>
                    <h4 className="mt-3 text-success">Operación Normal</h4>
                    <p className="text-muted">No hay alertas pendientes.</p>
                </div>
            ) : (
                <Row>
                    {processedTrips.map(t => {
                        const isVip = t.requested_vehicle_type?.name?.toLowerCase().includes('executive') || 
                                       t.requested_vehicle_type?.name?.toLowerCase().includes('luxury');
                        return (
                        <Col md={6} lg={4} xl={3} key={t.id} className="mb-4">
                            <Card className={`h-100 shadow border-0 ${t.alertLevel === 'critical' ? 'border-top border-5 border-danger' : 'border-top border-5 border-warning'}`}>
                                <Card.Body className="d-flex flex-column">
                                    <div className="d-flex justify-content-between mb-2">
                                        <Badge bg={t.alertLevel === 'critical' ? 'danger' : 'warning'} className="text-uppercase">
                                            {STATUS_LABELS[t.status] || t.status}
                                        </Badge>
                                        <span className="fw-bold text-muted d-flex align-items-center gap-1">
                                            #{t.id}
                                            {t.booking_incident_status === 'DISPUTED' && (
                                                <Badge bg="warning" text="dark" style={{ fontSize: '9px' }} title="Incidencia activa en Booking.com">
                                                    ⚠️ Disputa
                                                </Badge>
                                            )}
                                            {t.booking_incident_status === 'PENALIZED' && (
                                                <Badge bg="danger" style={{ fontSize: '9px' }} title="Penalización de Booking confirmada">
                                                    🔴 Penalizado
                                                </Badge>
                                            )}
                                            {t.booking_incident_status === 'RESOLVED' && (
                                                <Badge bg="success" style={{ fontSize: '9px' }} title="Incidencia resuelta a favor">
                                                    🟢 Reclamo Ganado
                                                </Badge>
                                            )}
                                            {t.booking_incident_status && t.booking_incident_dispute_sent && (
                                                <Badge bg="info" style={{ fontSize: '8px' }} title="Se enviaron respaldos / disputó en Partner Portal">
                                                    ✓ Respaldos
                                                </Badge>
                                            )}
                                            {isVip && (
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip>Servicio VIP ({t.requested_vehicle_type?.name})</Tooltip>}
                                                >
                                                    <span style={{cursor:'help'}} className="ms-1">⭐</span>
                                                </OverlayTrigger>
                                            )}
                                        </span>
                                    </div>
                                    <h5 className={`fw-bold mb-3 d-flex align-items-center gap-2 ${t.alertLevel === 'critical' ? 'text-danger' : 'text-warning'}`}>
                                        {t.alertLevel === 'critical' && <AlertCircle size={20} className="animate-pulse" />}
                                        {t.message}
                                    </h5>
                                    
                                    <div className="small mb-3">
                                        <div className="mb-1"><strong>Cliente:</strong> {t.client?.name}</div>
                                        <div className="mb-1 d-flex align-items-center gap-2">
                                            <strong>Pax:</strong> {t.passenger_name}
                                            {t.additional_passengers?.length > 0 && (
                                                <OverlayTrigger
                                                    trigger={['hover', 'focus']}
                                                    placement="top"
                                                    overlay={
                                                        <Popover className="shadow border-primary">
                                                            <Popover.Header as="h3" className="bg-primary text-white py-1 small fw-bold">Acompañantes</Popover.Header>
                                                            <Popover.Body className="py-1 px-2 small">
                                                                <ul className="list-unstyled mb-0">
                                                                    {t.additional_passengers.map(p => <li key={p.id}>• {p.full_name} {p.phone ? `(${p.phone})` : ''}</li>)}
                                                                </ul>
                                                            </Popover.Body>
                                                        </Popover>
                                                    }
                                                >
                                                    <Badge bg="info" pill style={{cursor:'help'}}>👥 {t.additional_passengers.length}</Badge>
                                                </OverlayTrigger>
                                            )}
                                            {t.notes && (
                                                <OverlayTrigger
                                                    trigger={['hover', 'focus']}
                                                    placement="top"
                                                    overlay={
                                                        <Popover className="shadow border-warning">
                                                            <Popover.Header as="h3" className="bg-warning text-dark py-1 small fw-bold">Notas</Popover.Header>
                                                            <Popover.Body className="py-1 px-2 small">{t.notes}</Popover.Body>
                                                        </Popover>
                                                    }
                                                >
                                                    <span style={{cursor:'help'}}>📝</span>
                                                </OverlayTrigger>
                                            )}
                                            {/* --- ICONOS DE EXTRAS --- */}
                                            {(t.extra_services || [])
                                                .filter(s => s.id !== 'parada_intermedia' && s.id !== 'reimbursable_expense')
                                                .map(s => {
                                                    const extraIconMap = {
                                                        baby_seat:      { icon: '👶', label: 'Silla de Bebé / Booster' },
                                                        water_still:    { icon: '💧', label: 'Agua Mineral sin Gas' },
                                                        water_sparkling:{ icon: '🫧', label: 'Agua Mineral con Gas' },
                                                        pet:            { icon: '🐾', label: 'Mascota Pequeña' },
                                                        extra_luggage:  { icon: '🧳', label: 'Maleta Extra' },
                                                    };
                                                    const info = extraIconMap[s.id] || { icon: '➕', label: s.name || s.id };
                                                    return (
                                                        <OverlayTrigger
                                                            key={s.id}
                                                            trigger={['hover', 'focus']}
                                                            placement="top"
                                                            overlay={<Tooltip>{info.label}</Tooltip>}
                                                        >
                                                            <span style={{cursor:'help', fontSize:'1rem'}}>{info.icon}</span>
                                                        </OverlayTrigger>
                                                    );
                                                })
                                            }
                                        </div>
                                        
                                        <div className="mb-2">
                                            <div className="fw-bold text-dark" style={{fontSize: '1.2rem', letterSpacing: '-0.5px'}}>
                                                {getFlagEmoji(t.origin_base?.timezone || t.timezone, t.local_currency_code, t.pickup_lat, t.pickup_lng, t.origin_base)} {t.localTimeStr} {t.isInternational ? 'LOCAL' : ''}
                                            </div>
                                            {t.isInternational && (
                                                <div className="text-muted fw-bold small d-flex align-items-center mt-1" style={{fontSize: '0.8rem'}}>
                                                    <Clock size={12} className="me-1" />
                                                    {t.adminTimeStr.split(' ')[1]} Chile
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {t.driver && (
                                        <div className="d-flex align-items-center mb-3 p-2 bg-light rounded border border-primary border-opacity-25">
                                            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{width:30, height:30}}>{t.driver.full_name[0]}</div>
                                            <div className="small lh-1 text-truncate flex-grow-1">
                                                <div className="fw-bold">{t.driver.full_name}</div>
                                                <div className="text-muted">{t.driver.phone || 'Sin tel'}</div>
                                            </div>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip>Ver en Mapa / GPS</Tooltip>}
                                            >
                                                <Button 
                                                    variant="primary" 
                                                    size="sm" 
                                                    className="p-1 rounded-circle ms-2" 
                                                    onClick={() => handleGoToFleetMonitor(t.driver_id)}
                                                >
                                                    <MapPin size={16} />
                                                </Button>
                                            </OverlayTrigger>
                                        </div>
                                    )}

                                    <div className="mt-auto d-grid gap-2">
                                        {t.status?.toLowerCase() === 'pending' && (
                                            <Button variant="danger" className="fw-bold" onClick={() => handleOpenAssign(t)}>🚨 ASIGNAR AHORA</Button>
                                        )}

                                        {['assigned', 'confirmed'].includes(t.status?.toLowerCase()) && (
                                            <div className="d-flex gap-2">
                                                <Button variant="outline-dark" className="w-50" onClick={() => handleContactDriver(t.driver)}>📞 Contactar</Button>
                                                <Button variant="info" className="w-50 text-white" onClick={() => handleAdvanceStatus(t, 'confirmed')}>👍 Confirmar</Button>
                                            </div>
                                        )}

                                        {t.status?.toLowerCase() === 'confirmed' && (
                                            <Button variant="primary" className="w-100 mt-1" onClick={() => handleAdvanceStatus(t, 'en_route')}>▶ Iniciar Ruta</Button>
                                        )}

                                        {t.status?.toLowerCase() === 'en_route' && (
                                            <div className="d-flex gap-2">
                                                <Button variant="outline-dark" className="w-50" onClick={() => handleContactDriver(t.driver)}>📞 Contactar</Button>
                                                <Button variant="success" className="w-50" onClick={() => handleAdvanceStatus(t, 'arrived')}>📍 Llegado</Button>
                                            </div>
                                        )}
                                        
                                        {t.status?.toLowerCase() === 'arrived' && (
                                            <Button variant="success" onClick={() => handleAdvanceStatus(t, 'on_board')}>👋 Pax a Bordo</Button>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    )})}
                </Row>
            )}

            {/* MODAL ASIGNACIÓN RÁPIDA */}
            <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} size="sm" centered>
                <Modal.Header closeButton className="bg-danger text-white"><Modal.Title>Asignación de Emergencia</Modal.Title></Modal.Header>
                <Modal.Body>
                    <p className="small mb-2">Viaje #{selectedTrip?.id} - {selectedTrip?.passenger_name}</p>
                    <Form.Group>
                        <Form.Label>Seleccionar Conductor (Solo Permitidos)</Form.Label>
                        <SearchableSelect 
                            options={permittedDrivers.map(d => {
                                const v = vehicles.find(veh => veh.current_driver_id === d.id);
                                let statusSuffix = '';
                                if (!d.has_active_session) {
                                    statusSuffix = ' (No Conectado)';
                                } else {
                                    const isStale = !d.last_heartbeat || (new Date().getTime() - new Date(d.last_heartbeat).getTime()) > 300000;
                                    if (isStale) {
                                        statusSuffix = ' (Sin Señal)';
                                    } else if (!d.is_online) {
                                        statusSuffix = ' (En Pausa)';
                                    }
                                }
                                return {
                                    id: d.id,
                                    full_name: `${d.full_name}${statusSuffix}`,
                                    internal_code: v?.internal_code,
                                    plate: d.assigned_vehicle_plate
                                };
                            })} 
                            value={selectedDriverId} 
                            onChange={val => setSelectedDriverId(val)} 
                            placeholder="Buscar Conductor..."
                        />
                        {permittedDrivers.length === 0 && <Form.Text className="text-danger">No hay móviles permitidos en esta zona.</Form.Text>}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAssignModal(false)}>Cancelar</Button>
                    <Button variant="danger" onClick={handleAssignDriver} disabled={!selectedDriverId}>Confirmar</Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Vista Previa de Importación Masiva */}
            <BulkImportPreviewModal
                show={showPreviewModal}
                onHide={() => setShowPreviewModal(false)}
                fileName={previewFileName}
                stats={previewStats}
                onConfirm={handleConfirmImport}
                loading={previewLoading}
            />
        </div>
    );
};

export default DispatchBoard;
